import React from 'react'
import { Virtuoso } from 'react-virtuoso'
import { Skeleton } from '@material-ui/lab'
import { TextField, Select, FormControl, FormHelperText } from '@material-ui/core'
import { PrimaryButton } from 'components'

type Option = { text: string; value: string }
export type MultiSelectProps = {
  id: string
  label?: string
  placeholder: string
  disabled?: boolean
  isLoading?: boolean
  enableSearch?: boolean
  onOpen?: () => void
  value: string[] | { value: string[]; text: string[] }
  onChange(value: string[], text: string[], isDefaultAll?: boolean): void
  options: Option[]
  error?: boolean
  helperText?: string
  onBlur?: () => void
}

export const MultiSelect = (props: MultiSelectProps) => {
  const {
    onBlur,
    onChange,
    onOpen,
    options,
    placeholder,
    value: innerValue,
    error,
    helperText,
    label,
    id,
    disabled,
    isLoading,
    enableSearch = false,
  } = props

  const { value, text } = React.useMemo(() => {
    return Array.isArray(innerValue)
      ? { value: [...innerValue], text: [...innerValue] }
      : { value: [...innerValue.value], text: [...innerValue.text] }
  }, [innerValue])

  const [open, setOpen] = React.useState(false)
  const [searchText, setSearchText] = React.useState<string>('')

  //Default select all items if initial state has 'all' only
  React.useEffect(() => {
    if (value.length === 1 && value[0] === 'all' && options.length > 0) {
      handleSelectAll(true, true)
    }
    // eslint-disable-next-line
  }, [options, value])

  const handleOpen = () => {
    setOpen(true)
    onOpen && onOpen()
  }

  const handleClose = () => {
    setSearchText('')
    setOpen(false)
  }

  const isSelected = (item: Option): boolean => {
    if (item.value === 'all') {
      return value.includes('all')
    }
    return value.includes(item.value)
  }

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>, selectedItem: { text: string; value: string }) => {
    if (event.target.name === 'all') {
      if (event.target.checked) {
        handleSelectAll(true)
        handleClose()
      } else {
        handleSelectAll(false)
      }
    } else {
      let newValue = value
      if (value.includes(selectedItem['value'])) {
        newValue = newValue.filter((item) => item !== selectedItem['value'] && item !== 'all')
      } else {
        newValue.push(selectedItem['value'])
        if (newValue.length === options.length) {
          newValue = ['all', ...newValue]
        }
      }

      let newText = text
      if (text.includes(selectedItem['text'])) {
        newText = newText.filter((item) => item !== selectedItem['text'] && item !== 'all')
      } else {
        newText.push(selectedItem['text'])
        if (newValue.includes('all')) {
          newText = ['all', ...newText]
        }
      }

      onChange(newValue, newText)
    }
  }

  const handleSelectAll = (selectAll: boolean, isDefaultAll?: boolean) => {
    if (selectAll) {
      const allValue = ['all', ...options.map((item) => item.value)]
      const allText = ['all', ...options.map((item) => item.text)]

      onChange(allValue, allText, isDefaultAll)
    } else {
      onChange([], [], isDefaultAll)
    }
  }

  const filteredOptions = React.useMemo((): Option[] => {
    // Search text with non alphanumeric characters stripped
    const strippedSearchText = searchText.toLowerCase().replace(/[^0-9a-z]/gi, '')

    let visibleOptions = options.filter((option) => {
      return option.text
        .toLowerCase()
        .replace(/[^0-9a-z]/gi, '')
        .includes(strippedSearchText)
    })
    if (!searchText) visibleOptions.unshift({ text: 'Select All', value: 'all' })

    return visibleOptions
  }, [options, searchText])

  const SearchBox = () => (
    <div className="w-full flex justify-center mb-2 px-2" key="searchBox">
      <TextField
        placeholder="Search..."
        fullWidth
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        autoFocus
      />
    </div>
  )

  const OptionItem = React.forwardRef(({ item }: { item: Option }, ref) => {
    return (
      <div className={`border-b border-gray-200 hover:bg-primary-lighter`}>
        <label htmlFor={item.value} className={`checkbox-container multi-select`}>
          <input
            className="mx-3"
            type="checkbox"
            id={item.value}
            name={item.value}
            checked={isSelected(item)}
            onChange={(event) => {
              handleSelect(event, item)
            }}
          />
          <span className="checkmark"></span>
          <span className={`whitespace-pre-wrap`}>{item.text}</span>
        </label>
      </div>
    )
  })

  const Items = () => {
    const optionComponents =
      filteredOptions.length > 1 || (filteredOptions.length === 1 && filteredOptions[0].value !== 'all') ? (
        <div
          className="max-h-64 lg:max-h-80"
          style={{
            minHeight: '120px',
            height: `${Math.min(filteredOptions.length * 53, 320)}px`,
          }}
        >
          <Virtuoso
            increaseViewportBy={{ top: 0, bottom: 200 }}
            totalCount={filteredOptions.length}
            itemContent={(index) => {
              const item = filteredOptions[index]
              return <OptionItem key={item.value} item={item} />
            }}
          />
        </div>
      ) : (
        <div className="p-4 text-center">No Data to Display</div>
      )

    return optionComponents
  }

  return (
    <FormControl className="flex flex-col" error={!!error}>
      {label && (
        <label className={`pb-4 text-lg font-semibold ${disabled ? 'text-body-lighter' : ''}`} htmlFor={id}>
          {label}
        </label>
      )}
      <Select
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        id={id}
        multiple
        value={value}
        variant="outlined"
        disabled={disabled}
        displayEmpty
        onBlur={() => {
          onBlur && onBlur()
        }}
        renderValue={() => {
          if (value.length === 0) {
            return <span className="font-light">{placeholder}</span>
          }
          if (text.includes('all')) {
            return <span>All</span>
          }

          return (
            options.length > 0 ? options.filter((item) => value.includes(item.value)).map((item) => item.text) : text
          ).join(', ')
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            style: {
              wordBreak: 'break-word',
            },
          },
          getContentAnchorEl: null,
          variant: 'menu',
        }}
        inputProps={{
          'data-testid': 'mui-select',
        }}
        style={{
          backgroundColor: 'white',
        }}
        SelectDisplayProps={{
          style: { backgroundColor: 'white' },
        }}
      >
        {isLoading ? (
          <div className="gap-y-2 p-2">
            <Skeleton variant="text" animation="wave" height="50px" />
            <Skeleton variant="text" animation="wave" height="50px" />
            <Skeleton variant="text" animation="wave" height="50px" />
          </div>
        ) : options?.length > 0 ? (
          <div>
            {enableSearch && SearchBox()}
            {Items()}
          </div>
        ) : (
          <div className="p-4 text-center">No Data to Display</div>
        )}
        <div className="text-center bg-black-5 py-2 -mb-2">
          <PrimaryButton size="medium" buttonText="Ok" onClick={handleClose} />
        </div>
      </Select>
      <FormHelperText className="absolute -bottom-6" error={!!error}>
        {helperText}
      </FormHelperText>
    </FormControl>
  )
}
